import { useState, useRef } from 'react';
import type { SocialFormat, SocialPlatform, SocialPostMeta } from '../../shared/types';
import { SOCIAL_PLATFORM_LABELS, analysisCreditCost } from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';

// ── Format icons ─────────────────────────────────────────

function PostIcon({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

function CarouselIcon({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="3" width="12" height="18" rx="1.5" />
      <path d="M2 6.5h4M2 12h4M2 17.5h4" strokeWidth={1} strokeOpacity={0.5} />
      <path d="M22 6.5h-4M22 12h-4M22 17.5h-4" strokeWidth={1} strokeOpacity={0.5} />
    </svg>
  );
}

function VideoIcon({ className = 'w-7 h-7' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M10 9l5 3-5 3V9z" />
    </svg>
  );
}

// ── Types ────────────────────────────────────────────────

interface Props {
  error: string | null;
  onSubmit: (data: {
    textContent?: string;
    context?: string;
    images?: File[];
    social: SocialPostMeta;
  }) => void;
  onBack: () => void;
  /** Crédits social_post disponibles (null = accès illimité via le plan → pas de compteur). */
  creditsAvailable?: number | null;
}

// ── Format cards ─────────────────────────────────────────

const FORMATS: Array<{
  key: SocialFormat;
  Icon: React.FC<{ className?: string }>;
  title: string;
  subtitle: string;
  description: string;
}> = [
  {
    key: 'post',
    Icon: PostIcon,
    title: 'Publication',
    subtitle: 'Post image unique',
    description: 'Analysez un post avec un visuel et sa légende avant publication. Verdict de conformité + suggestions de réécriture.',
  },
  {
    key: 'carousel',
    Icon: CarouselIcon,
    title: 'Carrousel',
    subtitle: 'Plusieurs slides ordonnées',
    description: 'Uploadez vos slides dans l\'ordre de publication. Chaque visuel est évalué individuellement et l\'ensemble est scoré.',
  },
  {
    key: 'video',
    Icon: VideoIcon,
    title: 'Story / Reel vidéo',
    subtitle: 'Légende + checklist',
    description: 'Analysez la légende et les hashtags. Une checklist de points à vérifier dans la vidéo elle-même est fournie.',
  },
];

const PLATFORMS: SocialPlatform[] = ['instagram', 'linkedin', 'facebook'];

function extractHashtagsAndMentions(caption: string): {
  hashtags: string[];
  mentions: string[];
} {
  const hashtags = Array.from(caption.matchAll(/#([\wÀ-ɏ-]+)/g)).map(
    (m) => `#${m[1]}`,
  );
  const mentions = Array.from(caption.matchAll(/@([\w.-]+)/g)).map(
    (m) => `@${m[1]}`,
  );
  return { hashtags, mentions };
}

// ── Image drop zone (shared) ─────────────────────────────

function ImageDropZone({
  multiple,
  label,
  hint,
  fileInputRef,
  onFiles,
}: {
  multiple: boolean;
  label: string;
  hint: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFiles: (files: FileList | null) => void;
}) {
  return (
    <div
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        e.currentTarget.classList.add('border-violet-500');
      }}
      onDragLeave={(e) => e.currentTarget.classList.remove('border-violet-500')}
      onDrop={(e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('border-violet-500');
        onFiles(e.dataTransfer.files);
      }}
      className="border-2 border-dashed border-slate-300 dark:border-slate-600 shader:border-white/20 rounded-xl p-6 text-center cursor-pointer hover:border-violet-400 transition-colors"
    >
      <p className="text-sm text-slate-600 dark:text-slate-400">{label}</p>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(e) => onFiles(e.target.files)}
        className="hidden"
      />
    </div>
  );
}

// ── Main component ───────────────────────────────────────

export default function SocialPostHub({ error, onSubmit, onBack, creditsAvailable = null }: Props) {
  const [format, setFormat] = useState<SocialFormat | null>(null);
  const [platform, setPlatform] = useState<SocialPlatform>('instagram');
  const [caption, setCaption] = useState('');
  const [context, setContext] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type),
    );
    setImages((prev) => [...prev, ...newFiles]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const moveImage = (from: number, direction: -1 | 1) => {
    const to = from + direction;
    if (to < 0 || to >= images.length) return;
    setImages((prev) => {
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  };

  // Coût en crédits de l'analyse courante (carrousel = paliers selon le nb de
  // slides ; post/vidéo = 1). Si un solde de crédits est imposé (creditsAvailable
  // non null) et qu'il est insuffisant, on BLOQUE le lancement.
  const submitCost = format ? analysisCreditCost('social_post', format, images.length) : 1;
  const enoughCredits = creditsAvailable === null || submitCost <= creditsAvailable;
  const canSubmit = (() => {
    if (!format || !enoughCredits) return false;
    if (format === 'post') return images.length >= 1;
    if (format === 'carousel') return images.length >= 2;
    if (format === 'video') return caption.trim().length > 0;
    return false;
  })();

  const handleSubmit = () => {
    if (!format || !canSubmit) return;

    const { hashtags, mentions } = extractHashtagsAndMentions(caption);
    const social: SocialPostMeta = {
      mode: 'analysis',
      format,
      platform,
      caption: caption.trim() || undefined,
      hashtags,
      mentions,
    };

    onSubmit({
      textContent: caption.trim() || undefined,
      context: context.trim() || undefined,
      images: format !== 'video' && images.length > 0 ? images : undefined,
      social,
    });
  };

  const carouselCreditCost = (format === 'carousel' && images.length >= 2)
    ? analysisCreditCost('social_post', 'carousel', images.length)
    : null;

  const submitLabel = (() => {
    if (format === 'carousel') {
      const cost = carouselCreditCost ? ` · ${carouselCreditCost} analyses` : '';
      return `Analyser ce carrousel${cost}`;
    }
    if (format === 'video') return 'Analyser cette vidéo';
    return 'Analyser ce post';
  })();

  // ── Back button (shared) ─────────────────────────────
  const BackButton = ({ onClick }: { onClick: () => void }) => (
    <button
      onClick={onClick}
      className="p-back p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors text-slate-500 shader:text-white/60"
      aria-label="Retour"
    >
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
      </svg>
    </button>
  );

  // ── Format selection screen ──────────────────────────
  if (!format) {
    return (
      <div className="animate-fade-in max-w-3xl mx-auto">
        {/* Mode pastel : bandeau à l'aplat de la typologie, icône seule dans
            une pastille blanche (cf. ContentUploader). */}
        <div className="p-hero flex items-center gap-3 mb-6">
          <BackButton onClick={onBack} />
          <div className="p-hero-pill hidden pastel:grid" aria-hidden="true">
            <AnalysisIcon type="social_post" className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-xl text-slate-900 dark:text-white pastel:text-(--p-ink) flex items-center gap-2">
              <AnalysisIcon type="social_post" className="w-5 h-5 text-slate-400 dark:text-white/50 pastel:hidden" />
              Post réseaux sociaux
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 pastel:text-(--p-ink)/70">
              Quel type de contenu souhaitez-vous analyser ?
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 stagger-children">
          {FORMATS.map((f) => {
            const Icon = f.Icon;
            return (
              <button
                key={f.key}
                onClick={() => setFormat(f.key)}
                className="glow-card group text-left p-6 rounded-xl border border-slate-200 dark:border-slate-700 shader:border-white/10 bg-white dark:bg-slate-900 shader:bg-white/5 shader:backdrop-blur-xl hover:border-transparent transition-all duration-300"
              >
                <Icon className="w-7 h-7 mb-3 text-slate-400 dark:text-white/50 group-hover:text-slate-600 dark:group-hover:text-white/80 transition-colors duration-300" />
                <h3 className="font-display text-slate-900 dark:text-white mb-1 transition-colors duration-300">
                  {f.title}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/40 group-hover:text-slate-700 dark:group-hover:text-white transition-colors duration-300">
                  {f.description}
                </p>
                <div className="mt-3 text-xs text-slate-400 dark:text-slate-500 shader:text-white/30">
                  {f.subtitle}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Active form ──────────────────────────────────────
  const activeFormat = FORMATS.find((f) => f.key === format)!;
  const ActiveIcon = activeFormat.Icon;

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <div className="p-hero flex items-center gap-3 mb-6">
        <BackButton onClick={() => { setFormat(null); setImages([]); setCaption(''); setContext(''); }} />
        <div className="p-hero-pill hidden pastel:grid" aria-hidden="true">
          <ActiveIcon className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-xl text-slate-900 dark:text-white pastel:text-(--p-ink) flex items-center gap-2">
            <ActiveIcon className="w-5 h-5 text-slate-400 dark:text-white/50 pastel:hidden" />
            {activeFormat.title}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 pastel:text-(--p-ink)/70">
            {activeFormat.description}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 shader:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Platform selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
          Plateforme <span className="text-slate-400 font-normal">(optionnel — ne modifie pas le verdict)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatform(p)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                platform === p
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'border-slate-300 dark:border-slate-600 shader:border-white/10 text-slate-700 dark:text-slate-300 hover:border-violet-400'
              }`}
            >
              {SOCIAL_PLATFORM_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Caption */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
          Légende{' '}
          {format !== 'video' && <span className="text-slate-400 font-normal">(optionnel) </span>}
          <span className="text-slate-400 font-normal">caption + hashtags + mentions</span>
        </label>
        <div className="glow-input">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={
              format === 'video'
                ? 'Collez ici la légende de votre vidéo, avec les hashtags et @mentions...'
                : 'Collez ici le texte complet du post, avec les hashtags et @mentions tels qu\'ils seront publiés...'
            }
            rows={4}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y min-h-[6rem]"
          />
        </div>
        {caption.trim() && (() => {
          const { hashtags, mentions } = extractHashtagsAndMentions(caption);
          return (hashtags.length > 0 || mentions.length > 0) ? (
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-3">
              {hashtags.length > 0 && (
                <span>{hashtags.length} hashtag{hashtags.length > 1 ? 's' : ''} détecté{hashtags.length > 1 ? 's' : ''}</span>
              )}
              {mentions.length > 0 && (
                <span>{mentions.length} mention{mentions.length > 1 ? 's' : ''} détectée{mentions.length > 1 ? 's' : ''}</span>
              )}
            </div>
          ) : null;
        })()}
      </div>

      {/* Publication — single image (required) */}
      {format === 'post' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
            Visuel du post <span className="text-red-500">*</span>
          </label>
          {images.length === 0 ? (
            <ImageDropZone
              multiple={false}
              label="Cliquez ou glissez-déposez le visuel du post"
              hint="JPG, PNG, WebP — 5 Mo max"
              fileInputRef={fileInputRef}
              onFiles={handleFiles}
            />
          ) : (
            <div className="flex items-start gap-3">
              <div className="relative group">
                <div className="w-24 h-24 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <img
                    src={URL.createObjectURL(images[0])}
                    alt={images[0].name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => removeImage(0)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
              <button
                type="button"
                onClick={() => setImages([])}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors mt-2"
              >
                Changer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Carousel — ordered slides (required 2+) */}
      {format === 'carousel' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Slides du carrousel <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            2 à 10 slides dans l'ordre de publication (slide 1 = couverture). Utilisez les flèches ◀▶ pour réordonner.
          </p>
          <ImageDropZone
            multiple={true}
            label="Cliquez ou glissez-déposez vos slides"
            hint="JPG, PNG, WebP — 5 Mo max par image, 10 images max"
            fileInputRef={fileInputRef}
            onFiles={handleFiles}
          />
          {images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {images.map((img, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="relative group">
                    <div className="w-20 h-20 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
                      <img
                        src={URL.createObjectURL(img)}
                        alt={img.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="absolute top-1 left-1 text-xs font-bold text-white bg-black/60 rounded px-1">
                      {i + 1}
                    </span>
                    <button
                      onClick={() => removeImage(i)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => moveImage(i, -1)}
                      disabled={i === 0}
                      className="w-6 h-6 rounded text-xs flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                      aria-label="Déplacer à gauche"
                    >
                      ◀
                    </button>
                    <button
                      type="button"
                      onClick={() => moveImage(i, 1)}
                      disabled={i === images.length - 1}
                      className="w-6 h-6 rounded text-xs flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                      aria-label="Déplacer à droite"
                    >
                      ▶
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {images.length > 0 && images.length < 2 && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Ajoutez au moins une deuxième slide pour lancer l'analyse.
            </p>
          )}
          {carouselCreditCost !== null && creditsAvailable !== null && (
            <div className={`mt-3 flex items-start gap-2 text-xs ${creditsAvailable >= carouselCreditCost ? 'text-slate-500 dark:text-slate-400 shader:text-white/50' : 'text-red-600 dark:text-red-400'}`}>
              <svg className="w-3.5 h-3.5 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
              </svg>
              {creditsAvailable >= carouselCreditCost ? (
                <span>Ce carrousel de {images.length} slides consomme <strong>{carouselCreditCost} analyses</strong> · solde&nbsp;: {creditsAvailable} &rarr; <strong>{creditsAvailable - carouselCreditCost}</strong> après.</span>
              ) : (
                <span>Ce carrousel consomme <strong>{carouselCreditCost} analyses</strong>, il vous en reste <strong>{creditsAvailable}</strong>. Réduisez le nombre de slides ou ajoutez un bundle.</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Context */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-2">
          Contexte <span className="text-slate-400 shader:text-white/40 font-normal">(optionnel — non analysé)</span>
        </label>
        <div className="glow-input">
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Ex: marque de whisky tourbé, cible 35-55 ans, France. Aide l'IA à interpréter votre contenu mais ne modifie pas le verdict."
            rows={2}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-none"
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="font-display w-full py-3.5 rounded-xl text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shader:from-white shader:to-white shader:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-violet-200 dark:shadow-violet-900/30"
      >
        {submitLabel}
      </button>
    </div>
  );
}
